/**
 * CSV import wizard for the rescue portal.
 *
 *   Step 1 — Get the template (download CSV, browse field list)
 *   Step 2 — Upload & review (dropzone → inline verification table)
 *   Step 3 — Done (success view)
 *
 * Parsing, validation, tooltips, icons, and the success view live in
 * sibling modules so this file stays focused on the wizard flow + Apollo
 * wiring. Publishing is all-or-nothing — any row error blocks submit.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ApolloError, useMutation, useQuery } from '@apollo/client';
import Modal, { ModalTitle, ModalSubtitle } from '../../../components/Modal/Modal';
import StepProgress from '../../../components/StepProgress/StepProgress';
import useChipClaimEligibility from '../../../hooks/useChipClaimEligibility';
import { allBreedsQuery, type AllBreedsData, rescuePetsQuery } from '../../../lib/rescueQueries';
import { chipListsFirstPageRefetch } from '../../../lib/chipListsRefetch';
import {
  rescueImportPetsWithChipsMutation,
  rescueResellerStatsQuery,
  saveRescueImportFileMutation,
  type RescueImportPetsWithChipsData,
  type RescueImportPetWithChipRowInput,
  type RescueImportPetsWithChipsRowResult,
  type SaveRescueImportFileData,
} from '../../../lib/integrationQueries';
import type { Species } from '../../../../types/gql-op-types';
import {
  trackImportAbandoned,
  trackImportAuditFailed,
  trackImportFailed,
  trackImportFileParsed,
  trackImportFileRejected,
  trackImportPublished,
  trackImportRolledBack,
  trackImportWizardOpened,
  type ImportFileParsedProps,
} from '../../../analytics';
import styles from '../Integrations.module.scss';
import { type CsvHeader, isCsvHeader, MAX_FILE_BYTES, MAX_ROWS, REQUIRED_HEADERS, formatBytes } from './constants';
import { CsvParseError, parseCsv } from './parseCsv';
import {
  displayRowNumber,
  type ParsedRow,
  toVerifiedRow,
  validateAndMapRow,
  type ValidationError,
} from './validateRow';
import TemplateIntro from './TemplateIntro';
import UploadAndReview from './UploadAndReview';
import ImportSuccess from './ImportSuccess';
import ImportFailed from './ImportFailed';

/**
 * Categorize a mutation-thrown error for analytics. Apollo wraps every
 * failure mode (network, transport, GraphQL validation, top-level server
 * error) in `ApolloError` whose `.name` is always `'ApolloError'`, so
 * `err.name` alone collapses every distinct failure into one bucket.
 *
 *   network_error          — transport failed (offline, DNS, 5xx without body)
 *   graphql_<code>         — server returned `extensions.code` (e.g. `BAD_USER_INPUT`)
 *   graphql_error          — server returned a GraphQL error with no code
 *   <error_constructor>    — non-Apollo Error (e.g. `TypeError` from client code)
 *   unknown                — non-Error throw
 */
function categorizeMutationError(err: unknown): string {
  if (err instanceof ApolloError) {
    if (err.networkError) {
      return 'network_error';
    }
    const code = err.graphQLErrors[0]?.extensions?.code;
    if (typeof code === 'string' && code.length > 0) {
      return `graphql_${code.toLowerCase()}`;
    }
    return 'graphql_error';
  }
  if (err instanceof Error) {
    return err.name || 'error';
  }
  return 'unknown';
}

interface ImportPetsModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * Fires when the backend committed the batch. The import is
   * all-or-nothing — any row failure rolls back the entire transaction
   * — so this only ever fires on a clean batch; the failure view
   * handles rollback inline and the parent shouldn't react.
   */
  onSuccess: () => void;
}

export default function ImportPetsModal({ open, onClose, onSuccess }: ImportPetsModalProps) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [isDragging, setIsDragging] = useState(false);
  const [fileSizeBytes, setFileSizeBytes] = useState<number | null>(null);
  const [filterMode, setFilterMode] = useState<'all' | 'errors'>('all');
  // Raw CSV text — kept in state so we can hand the original file off to
  // `saveRescueImportFile` after a successful import. Not used for the
  // parsing path (which works on the already-parsed `parsedRows`).
  const [originalCsv, setOriginalCsv] = useState<string>('');
  const [fileName, setFileName] = useState<string | null>(null);
  // `parseError` covers upload-time failures (size cap, malformed CSV,
  // unknown columns). `submitError` covers mutation failures from
  // `rescueImportPetsWithChips` — separated so the two render in
  // different slots and don't conflate.
  const [parseError, setParseError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
  const [resultRows, setResultRows] = useState<RescueImportPetsWithChipsRowResult[]>([]);
  const [successCount, setSuccessCount] = useState(0);
  // True when the backend rejected the batch — no writes applied, every
  // failing row is in `resultRows.errors[]`. Drives the step-3 failure
  // view. Import is all-or-nothing, so this is the sole discriminant
  // between the success and failure views.
  const [validationFailed, setValidationFailed] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Sequence token for in-flight `handleFile` reads. Bumping invalidates
  // any earlier read that's still mid-async — fixes rapid re-pick races.
  const fileSeqRef = useRef(0);
  // Tracks whether the user actually published at least one pet this
  // session. Used by the close handler to skip "abandoned" analytics —
  // a closed-after-full-rollback view IS abandonment of the recovery
  // flow (the user got nothing and didn't try again), so we leave this
  // false in that case and let the abandoned event fire alongside the
  // already-emitted `rolled_back` event.
  const publishedRef = useRef(false);
  useEffect(() => {
    if (open) {
      publishedRef.current = false;
    }
  }, [open]);

  // After a successful import we need to invalidate the chip lists the
  // Chips/Dashboard pages serve from the Apollo cache — otherwise the
  // freshly-imported chips don't appear until the next full page mount.
  // Uses the shared `chipListsFirstPageRefetch` entries so the page-0
  // variables can't drift from what the Chips page actually subscribes
  // to. `rescuePetsQuery` is included because the import creates new
  // pets and re-homes them to the new owner's household; the pet →
  // device map driving Add Owner would otherwise serve stale pairings.
  const [importPets, { loading: importing }] = useMutation<RescueImportPetsWithChipsData>(
    rescueImportPetsWithChipsMutation,
    {
      refetchQueries: [{ query: rescueResellerStatsQuery }, ...chipListsFirstPageRefetch, { query: rescuePetsQuery }],
    },
  );
  // Best-effort audit log. We don't expose its loading state to the UI:
  // a failure here is non-fatal (the pets are already imported).
  const [saveImportFile] = useMutation<SaveRescueImportFileData>(saveRescueImportFileMutation);

  // Fetch the master breed list (across all species) for breed-name
  // validation. Cache-first; shared with PetFormModal and RegisterChipModal.
  //
  // `breedsError` is surfaced so the verifier doesn't false-flag every
  // breed-bearing row as "not a recognized breed" when the query itself
  // failed (empty `breedNameMap` would otherwise match every breed
  // against an empty set). Publish is gated on it for the same reason.
  const {
    data: breedsData,
    loading: breedsLoading,
    error: breedsError,
    refetch: refetchBreeds,
  } = useQuery<AllBreedsData>(allBreedsQuery, {
    fetchPolicy: 'cache-first',
    skip: !open,
  });
  // Lower-cased name → set of species the breed is registered under. Used
  // both to confirm "is this a real breed?" and to enforce species/breed
  // consistency when the row specifies both. Value type is `Set<Species>`
  // (the enum) — the API hands back species as a free-form string, but
  // every valid row stores one of the enum tokens.
  const breedNameMap = useMemo(() => {
    const map = new Map<string, Set<Species>>();
    for (const b of breedsData?.allBreeds ?? []) {
      const key = b.name.trim().toLowerCase();
      if (!key) {
        continue;
      }
      const set = map.get(key) ?? new Set<Species>();
      set.add(b.species as Species);
      map.set(key, set);
    }
    return map;
  }, [breedsData]);

  // Every chip serial in the CSV, deduplicated and uppercased. Fed to
  // `useChipClaimEligibility` to classify each as already-mine, claimable
  // (the import will register it to this rescue), or owned-by-another
  // (rejected). One bulk call replaces what used to be a paginate-every-
  // owned-chip + per-unowned-serial-lookup combo.
  const allChipSerials = useMemo<readonly string[]>(() => {
    if (parsedRows.length === 0) {
      return [];
    }
    const set = new Set<string>();
    for (const r of parsedRows) {
      const key = r.chipSerialNumber.toUpperCase();
      if (key) {
        set.add(key);
      }
    }
    return [...set];
  }, [parsedRows]);
  const {
    results: claimEligibility,
    loading: claimEligibilityLoading,
    error: claimEligibilityError,
    refetch: refetchClaimEligibility,
  } = useChipClaimEligibility(allChipSerials, !open);

  // Reset every piece of working state EXCEPT the step token. Called from
  // open, close, and "Upload another" so adding a new field requires
  // touching one place. Step transitions are caller-specific (open → 1,
  // upload-another → 2) and stay at the call site.
  function resetWorkingState() {
    setFileName(null);
    setFileSizeBytes(null);
    setParseError(null);
    setSubmitError(null);
    setParsedRows([]);
    setResultRows([]);
    setSuccessCount(0);
    setValidationFailed(false);
    setIsDragging(false);
    setFilterMode('all');
    setOriginalCsv('');
  }

  useEffect(() => {
    if (open) {
      setStep(1);
      resetWorkingState();
      trackImportWizardOpened();
    } else {
      // Modal closed — defense-in-depth in case the parent keeps this
      // component mounted with `open: false` indefinitely. Open also
      // resets, but we don't want owner PII (email/phone/address) to
      // sit in React memory between sessions.
      resetWorkingState();
    }
    // resetWorkingState is stable for our purposes — it captures setters
    // which never change identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Cross-row checks are applied here rather than in `validateAndMapRow`
  // because they depend on context the row-level validator doesn't have:
  // (a) the full set of CSV rows for duplicate-detection, and (b) the
  // rescue's owned chip serials for ownership verification.
  const verifiedRows = useMemo<ParsedRow[]>(() => {
    if (parsedRows.length === 0) {
      return parsedRows;
    }
    const seen = new Map<string, number[]>();
    for (const r of parsedRows) {
      const key = r.chipSerialNumber.toUpperCase();
      if (!key) {
        continue;
      }
      const list = seen.get(key);
      if (list) {
        list.push(r.rowIndex);
      } else {
        seen.set(key, [r.rowIndex]);
      }
    }
    return parsedRows.map((r) => {
      const extra: ValidationError[] = [];
      const key = r.chipSerialNumber.toUpperCase();
      const dupes = key ? (seen.get(key) ?? []) : [];
      if (dupes.length > 1) {
        const others = dupes.filter((idx) => idx !== r.rowIndex).map(displayRowNumber);
        extra.push({
          field: 'microchip_id',
          code: 'duplicate',
          message: `microchip_id duplicates row${others.length > 1 ? 's' : ''} ${others.join(', ')}`,
        });
      }
      // Chip resolution: five-way decision per chip, driven by the
      // bulk eligibility lookup.
      //   `in_my_inventory` → existing unattached chip; pet will register
      //                       against it.
      //   `claim`           → chip will be registered to this rescue.
      //   `other_reseller`  → ERROR; cannot claim.
      //   `already_linked`  → ERROR; chip is already attached to a pet,
      //                       backend's pre-flight would reject.
      //   `not_usable`      → ERROR; chip exists but its condition
      //                       (recalled, damaged, etc.) blocks import.
      //   undefined         → lookup still pending; `verifying` gates
      //                       Publish so this won't be submitted early.
      if (key) {
        const status = claimEligibility.get(key);
        if (status === 'other_reseller') {
          extra.push({
            field: 'microchip_id',
            code: 'owned_by_other_reseller',
            message: 'microchip_id is already registered to another reseller',
          });
        } else if (status === 'already_linked') {
          extra.push({
            field: 'microchip_id',
            code: 'already_linked',
            message: 'microchip_id is already attached to a pet',
          });
        } else if (status === 'not_usable') {
          extra.push({
            field: 'microchip_id',
            code: 'not_usable',
            message: 'microchip_id is not in a condition that allows import',
          });
        }
      }
      // Breed check: the backend resolves breed name → breed id, so the name
      // has to match an entry in `allBreeds`. When species is also supplied,
      // the breed must be registered under that species (a "Labrador" row
      // with species=CAT is rejected). Gated on `breedsLoading` AND
      // `breedsError`: when the breeds query itself failed, `breedNameMap`
      // is empty and would false-flag every breed; we surface the error
      // separately and gate Publish on it instead. An empty `breedNameMap`
      // after a successful (non-erroring) load still means "checked" and
      // correctly rejects unknown breeds.
      if (r.breed && !breedsLoading && !breedsError) {
        const breedKey = r.breed.trim().toLowerCase();
        const speciesSet = breedNameMap.get(breedKey);
        if (!speciesSet) {
          extra.push({
            field: 'breed',
            code: 'unknown',
            message: `breed "${r.breed}" is not a recognized breed`,
          });
        } else if (r.species && !speciesSet.has(r.species as Species)) {
          extra.push({
            field: 'breed',
            code: 'invalid_format',
            message: `breed "${r.breed}" is not valid for species ${r.species}`,
          });
        }
      }
      if (extra.length === 0) {
        return r;
      }
      return { ...r, rawErrors: [...r.rawErrors, ...extra] };
    });
  }, [parsedRows, claimEligibility, breedNameMap, breedsLoading, breedsError]);

  const validRowCount = useMemo(() => verifiedRows.filter((r) => r.rawErrors.length === 0).length, [verifiedRows]);
  const invalidRowCount = verifiedRows.length - validRowCount;
  const duplicateRowCount = useMemo(
    () => verifiedRows.filter((r) => r.rawErrors.some((e) => e.code === 'duplicate')).length,
    [verifiedRows],
  );
  // Rows whose chip will be CLAIMED (not just re-linked) during import.
  // Shown in the summary so the rescue knows publishing will assign new
  // chips to their account.
  const claimRowCount = useMemo(() => {
    let n = 0;
    for (const r of verifiedRows) {
      if (r.rawErrors.length > 0) {
        continue;
      }
      const key = r.chipSerialNumber.toUpperCase();
      if (key && claimEligibility.get(key) === 'claim') {
        n += 1;
      }
    }
    return n;
  }, [verifiedRows, claimEligibility]);

  const displayedRows = useMemo(
    () => (filterMode === 'errors' ? verifiedRows.filter((r) => r.rawErrors.length > 0) : verifiedRows),
    [verifiedRows, filterMode],
  );

  function rejectFile(reason: string, message: string) {
    setParseError(message);
    setParsedRows([]);
    trackImportFileRejected(reason);
  }

  async function handleFile(file: File) {
    // Tag this run. A later `handleFile` bumps the ref, and we bail out at
    // every async boundary if our token is stale — preventing a slow read
    // of file A from clobbering state already advanced by file B.
    const seq = ++fileSeqRef.current;
    const isStale = () => fileSeqRef.current !== seq;

    setParseError(null);
    setFileName(file.name);
    setFileSizeBytes(file.size);
    setFilterMode('all');
    // Extension check up front — the `<input accept=".csv">` hint is
    // ignored by drag-and-drop and is a hint anyway, so the user can
    // hand us anything. Reject by name extension (case-insensitive)
    // rather than MIME type because `file.type` is unreliable across
    // platforms (often empty, sometimes `application/vnd.ms-excel`).
    if (!/\.csv$/i.test(file.name)) {
      const ext = file.name.includes('.') ? file.name.slice(file.name.lastIndexOf('.')) : '';
      const got = ext ? `a ${ext} file` : 'a file with no extension';
      rejectFile('wrong_file_type', `Only .csv files can be imported — you uploaded ${got} (${file.name}).`);
      return;
    }
    // Hard size cap BEFORE we read the file into memory — a multi-MB CSV
    // would block the main thread on `file.text()` and can OOM the tab.
    if (file.size > MAX_FILE_BYTES) {
      rejectFile(
        'size_exceeded',
        `File too large — limit is ${formatBytes(MAX_FILE_BYTES)} (got ${formatBytes(file.size)})`,
      );
      return;
    }
    try {
      const text = await file.text();
      if (isStale()) {
        return;
      }
      const matrix = parseCsv(text);
      if (matrix.length < 2) {
        rejectFile('empty_csv', 'CSV is empty or only has a header row');
        return;
      }
      const headerRow = matrix[0].map((h) => h.trim());
      const unknown = headerRow.filter((h) => !isCsvHeader(h));
      if (unknown.length > 0) {
        rejectFile('unknown_column', `Unknown column${unknown.length > 1 ? 's' : ''}: ${unknown.join(', ')}`);
        return;
      }
      // Duplicate-header check: `headers.indexOf(key)` in the row mapper
      // picks the first match and silently drops every later column with
      // the same name. Reject upfront so two `microchip_id` columns
      // can't quietly discard data.
      const duplicates = Array.from(new Set(headerRow.filter((h, i) => headerRow.indexOf(h) !== i)));
      if (duplicates.length > 0) {
        rejectFile('duplicate_column', `Duplicate column${duplicates.length > 1 ? 's' : ''}: ${duplicates.join(', ')}`);
        return;
      }
      const missing = REQUIRED_HEADERS.filter((h) => !headerRow.includes(h));
      if (missing.length > 0) {
        rejectFile(
          'missing_required_column',
          `Missing required column${missing.length > 1 ? 's' : ''}: ${missing.join(', ')}`,
        );
        return;
      }
      if (matrix.length - 1 > MAX_ROWS) {
        rejectFile('row_count_exceeded', `Too many rows — limit is ${MAX_ROWS} per upload (got ${matrix.length - 1})`);
        return;
      }
      // Stored rowIndex matches the backend's per-row index (file line
      // number — header is line 1, so data rows start at 2). Keeps the
      // `ImportFailed` join keyed by the same value the backend echoes
      // back. The user-facing label is rendered via
      // `displayRowNumber(rowIndex)` so the table reads as 1-based data
      // rows.
      const rows = matrix.slice(1).map((cells, i) => validateAndMapRow(headerRow as CsvHeader[], cells, i + 2));
      // Stash the original CSV only after all validation passes — we
      // only ever send it to `saveRescueImportFile` on submit success,
      // so there's no reason to hold it during a rejected upload.
      setOriginalCsv(text);
      setParsedRows(rows);
      // Stay on step 2 — the verification table renders inline once rows are
      // parsed. No separate "verify" step.
      // Aggregate error codes (counts only — no PII) for analytics.
      // Duplicate detection has to happen here, not in the per-row
      // validator: duplicates are a cross-row concern, and the `verifiedRows`
      // memo that attaches duplicate errors only runs after this event has
      // already fired. Mirror its seen-map pass so valid_count and
      // duplicate_count line up with what the user actually sees in the
      // review table.
      const serialCounts = new Map<string, number>();
      for (const row of rows) {
        const key = row.chipSerialNumber.toUpperCase();
        if (!key) {
          continue;
        }
        serialCounts.set(key, (serialCounts.get(key) ?? 0) + 1);
      }
      const codeCounts: Record<string, number> = {};
      let validCount = 0;
      let dupCount = 0;
      for (const row of rows) {
        const key = row.chipSerialNumber.toUpperCase();
        const isDup = key !== '' && (serialCounts.get(key) ?? 0) > 1;
        if (isDup) {
          dupCount += 1;
        }
        if (row.rawErrors.length === 0 && !isDup) {
          validCount += 1;
        }
        for (const e of row.rawErrors) {
          codeCounts[e.code] = (codeCounts[e.code] ?? 0) + 1;
        }
      }
      if (dupCount > 0) {
        codeCounts.duplicate = (codeCounts.duplicate ?? 0) + dupCount;
      }
      const parsedProps: ImportFileParsedProps = {
        row_count: rows.length,
        valid_count: validCount,
        invalid_count: rows.length - validCount,
        duplicate_count: dupCount,
        file_size_bytes: file.size,
        error_codes: Object.keys(codeCounts).length > 0 ? codeCounts : undefined,
      };
      trackImportFileParsed(parsedProps);
    } catch (err) {
      if (isStale()) {
        return;
      }
      if (err instanceof CsvParseError) {
        rejectFile('malformed_csv', err.message);
      } else {
        rejectFile('read_error', err instanceof Error ? err.message : 'Failed to read file');
      }
    }
  }

  async function handleConfirm() {
    // Frontend gate: refuse to submit a batch with any client-side
    // validation errors, or while verification queries are still in
    // flight / failed. The Publish button is already disabled in
    // these states — this is belt-and-suspenders against a programmatic
    // call sneaking through (e.g. someone wiring up an Enter shortcut
    // later). The backend runs its own two-phase validation (pre-flight
    // + per-row apply); this guard is purely about not wasting a
    // round-trip on a known-bad set.
    if (publishDisabled) {
      return;
    }
    if (verifiedRows.length === 0 || verifiedRows.some((r) => r.rawErrors.length > 0)) {
      return;
    }
    // Note: we do NOT refetch ownership chips immediately before submit.
    // The Apollo cache snapshot drives the verification UI; a refetch
    // here wouldn't propagate into the closure-captured `verifiedRows`
    // anyway. The backend re-validates chip ownership atomically per
    // row, so a chip that was recalled between upload and submit will
    // come back as a per-row error rather than silently slipping through.
    setSubmitError(null);
    // `toVerifiedRow` centralizes the species/gender/yearOfBirth narrowing
    // so we don't sprinkle `as` casts here. The gate above guarantees no
    // row throws.
    const pets: RescueImportPetWithChipRowInput[] = verifiedRows.map(toVerifiedRow);
    try {
      const { data } = await importPets({ variables: { input: { pets } } });
      const result = data?.rescueImportPetsWithChips;
      if (!result) {
        setSubmitError('Server returned no result');
        trackImportFailed('empty_response');
        return;
      }
      // Defensive: a well-behaved backend always returns at least one
      // row in `result.rows`. If we see an empty array we have no way to
      // render either step-3 view meaningfully — fall back to the
      // submit-error path so the user sees something actionable.
      if (result.rows.length === 0) {
        setSubmitError('Server returned an empty result. Please try again.');
        trackImportFailed('empty_result_rows');
        return;
      }
      // All-or-nothing: `validationFailed` is the sole discriminant.
      // On success every submitted row committed; on failure none did.
      setSuccessCount(result.successCount);
      setResultRows(result.rows);
      setValidationFailed(result.validationFailed);
      // On success drop `parsedRows` so owner PII doesn't sit in React
      // state through step 3. On failure keep it alive — ImportFailed
      // joins backend per-row errors to pet/owner identity by rowIndex.
      // `resetWorkingState` clears it on the next open or upload-another.
      if (!result.validationFailed) {
        setParsedRows([]);
      }
      setStep(3);
      // Suppress abandoned analytics only when the batch actually
      // committed. A user closing the failure view IS abandoning the
      // recovery flow — the abandoned event fires alongside the
      // already-emitted rolled_back event so the funnel reflects both.
      publishedRef.current = !result.validationFailed;
      if (result.validationFailed) {
        trackImportRolledBack({
          row_count: verifiedRows.length,
          error_count: result.errorCount,
        });
      } else {
        trackImportPublished({
          row_count: verifiedRows.length,
          claim_count: claimRowCount,
        });
        onSuccess();
      }
      // Best-effort audit save. Fire-and-forget — failure here does not
      // unwind the import. We save every attempt (success or failure)
      // so the rescue has a complete record of what they uploaded.
      if (originalCsv && fileName) {
        const importedPetIds = result.rows.map((r) => r.petId).filter((id): id is string => !!id);
        // Snapshot the content into a local before clearing state. The
        // mutation closes over this snapshot so the in-flight save
        // doesn't see the cleared value.
        const fileContent = originalCsv;
        const inputFileName = fileName;
        // Release PII from state immediately — the in-flight save holds
        // its own copy in the closure. If the save fails, we accept the
        // tradeoff: a partial audit trail vs. lingering owner PII in
        // React state through the success step.
        setOriginalCsv('');
        saveImportFile({
          variables: {
            input: {
              fileName: inputFileName,
              fileContent,
              rowCount: verifiedRows.length,
              successCount: result.successCount,
              errorCount: result.errorCount,
              importedPetIds,
            },
          },
        }).catch((err: unknown) => {
          // eslint-disable-next-line no-console
          console.warn('saveRescueImportFile failed (non-fatal):', err);
          // Telemetry: send the Error constructor name only — backend
          // error messages can carry identifiers. Lets ops monitor
          // audit-save failure rate without leaking PII.
          trackImportAuditFailed(err instanceof Error ? err.name : 'unknown');
        });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Import failed';
      setSubmitError(message);
      // Send a categorical reason to analytics — not the raw backend
      // message, which could include user-supplied identifiers in
      // error text (e.g. "chip 981… already linked"). User still sees
      // the full message in `submitError`. Apollo wraps every failure
      // mode in `ApolloError` (so `err.name` is always `'ApolloError'`);
      // distinguish network vs. GraphQL vs. extension-coded errors.
      trackImportFailed(categorizeMutationError(err));
    }
  }

  const stepLabels: Record<1 | 2 | 3, string> = {
    1: 'Get the template',
    2: 'Upload & review',
    3: 'Done',
  };

  const hasParsed = parsedRows.length > 0;
  // `verifying` gates the Publish button while either verification
  // query is still in flight — breeds or per-chip claim eligibility.
  // Once both settle, the row-level errors are authoritative.
  const verifying = breedsLoading || claimEligibilityLoading;

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFile(file);
    }
  }

  function handleUploadAnother() {
    // Reset everything except the breed/chip caches (they're tied to the
    // open Apollo session) and bounce back to the upload step so the user
    // can drop the next file.
    setStep(2);
    resetWorkingState();
    // Re-arm abandonment tracking for the next import attempt.
    publishedRef.current = false;
  }

  // Compute disable + label for the Publish button. We block publish when
  // any row error remains AND when either the breed list OR the chip
  // eligibility lookup failed to load — running validation against an
  // empty breed map false-flags every row with a breed, and silently
  // claiming through a failed eligibility check lets bad rows reach the
  // backend (which would reject the whole batch under all-or-nothing).
  const breedsBroken = !!breedsError;
  const publishDisabled =
    importing ||
    verifying ||
    breedsBroken ||
    claimEligibilityError ||
    !hasParsed ||
    invalidRowCount > 0 ||
    validRowCount === 0;
  let publishLabel: string;
  if (importing) {
    publishLabel = 'Publishing…';
  } else if (verifying) {
    publishLabel = 'Verifying…';
  } else if (breedsBroken) {
    publishLabel = "Couldn't verify breeds";
  } else if (claimEligibilityError) {
    publishLabel = "Couldn't verify chips";
  } else if (!hasParsed) {
    publishLabel = 'Publish';
  } else if (invalidRowCount > 0) {
    publishLabel = `Fix ${invalidRowCount} error${invalidRowCount === 1 ? '' : 's'} to publish`;
  } else {
    publishLabel = `Publish ${validRowCount} pet${validRowCount === 1 ? '' : 's'}`;
  }

  // Gates ALL close paths on `importing`. The Modal component routes
  // backdrop click and Escape through `onClose` — without this, the
  // user could trigger a server-side commit and then close before we
  // record the audit row, leaving a no-confirmation orphan. Also fires
  // the abandonment analytics event for closes that didn't commit a
  // batch (mid-flow or rolled-back).
  function safeClose() {
    if (importing) {
      return;
    }
    if (!publishedRef.current) {
      trackImportAbandoned(step, parsedRows.length > 0);
    }
    onClose();
  }

  // All-or-nothing: `validationFailed` is the sole discriminant
  // between the success and failure step-3 views.
  const isFailureView = step === 3 && validationFailed;

  return (
    <Modal open={open} onClose={safeClose} size="xx-wide" showCloseX={!importing} dismissOnBackdropClick={false}>
      <ModalTitle>
        {step === 1 && 'Import existing registrations'}
        {step === 2 && 'Upload & review'}
        {step === 3 && (isFailureView ? 'Import failed' : <span className={styles.srOnly}>Import complete</span>)}
      </ModalTitle>
      {step === 1 && (
        <ModalSubtitle>Bring over pets you already have in Clinic HQ, ShelterLuv, or a spreadsheet.</ModalSubtitle>
      )}
      {step === 2 && (
        <ModalSubtitle>
          {hasParsed ? (
            <>
              {verifiedRows.length} row{verifiedRows.length === 1 ? '' : 's'} from <strong>{fileName}</strong>
            </>
          ) : (
            <>Upload the template you filled in &mdash; we&rsquo;ll check every row.</>
          )}
        </ModalSubtitle>
      )}
      <StepProgress
        currentStep={step}
        totalSteps={3}
        labelSuffix={step === 3 ? undefined : stepLabels[step]}
        labelOverride={step === 3 ? (isFailureView ? 'Failed' : 'Done') : undefined}
      />

      {step === 1 && <TemplateIntro onCancel={safeClose} onNext={() => setStep(2)} />}

      {step === 2 && (
        <UploadAndReview
          fileInputRef={fileInputRef}
          fileName={fileName}
          fileSizeBytes={fileSizeBytes}
          isDragging={isDragging}
          setIsDragging={setIsDragging}
          handleFile={handleFile}
          handleDrop={handleDrop}
          onReplaceFile={resetWorkingState}
          hasParsed={hasParsed}
          parseError={parseError}
          submitError={submitError}
          breedsBroken={breedsBroken}
          refetchBreeds={refetchBreeds}
          eligibilityBroken={claimEligibilityError}
          refetchEligibility={refetchClaimEligibility}
          verifiedRows={verifiedRows}
          displayedRows={displayedRows}
          validRowCount={validRowCount}
          invalidRowCount={invalidRowCount}
          duplicateRowCount={duplicateRowCount}
          claimRowCount={claimRowCount}
          verifying={verifying}
          importing={importing}
          publishDisabled={publishDisabled}
          publishLabel={publishLabel}
          filterMode={filterMode}
          setFilterMode={setFilterMode}
          onBack={() => setStep(1)}
          onPublish={handleConfirm}
        />
      )}

      {step === 3 &&
        (isFailureView ? (
          <ImportFailed
            resultRows={resultRows}
            parsedRows={parsedRows}
            fileName={fileName}
            onClose={safeClose}
            onUploadAnother={handleUploadAnother}
          />
        ) : (
          <ImportSuccess
            successCount={successCount}
            resultRows={resultRows}
            fileName={fileName}
            onClose={safeClose}
            onUploadAnother={handleUploadAnother}
          />
        ))}

      {importing && (
        <div className={styles.processingOverlay} role="status" aria-live="polite" aria-busy="true">
          <div className={styles.processingOverlayInner}>
            <svg
              className={styles.processingSpinner}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="10" opacity="0.25" />
              <path d="M12 2a10 10 0 0 1 10 10" />
            </svg>
            <div className={styles.processingLabel}>
              Publishing {validRowCount} pet{validRowCount === 1 ? '' : 's'}…
            </div>
            <div className={styles.processingSubLabel}>
              This can take a moment — please don&rsquo;t close the window.
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
